import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import { Alert, Descriptions, Space, Tag, Tooltip, Typography } from 'antd'
import {
  FieldCategory,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { ComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import {
  DynamicFieldTooltip,
  TooltipConfig,
} from 'features/libra/stepComponents/niiScreening/tooltips/DynamicFieldTooltip'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { ComputedOutcomeModal } from './ComputedOutcomeModal'
import {
  isTriStateFunction,
  isWeightedScoringFunction,
} from './computeFunctionRegistry'
import { useComputedSectionValue } from './useComputedSectionValue'

const TwoColumn = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
`

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`

const EditRow = styled.div`
  width: 100%;
  max-width: 360px;
`

const OutcomeSplash = styled.div<{ $pass: boolean }>`
  display: flex;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 12px;
  width: 100%;
  max-width: 360px;
  border: 1px solid ${({ $pass }) => ($pass ? '#52c41a' : '#ff4d4f')};
  background: ${({ $pass }) =>
    $pass ? 'rgba(82, 196, 26, 0.08)' : 'rgba(255, 77, 79, 0.08)'};
`

const OutcomeLabel = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`

const LabelWithTooltipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

type Tri = 'low' | 'med' | 'high'

function isTriValue(value: unknown): value is Tri {
  return value === 'low' || value === 'med' || value === 'high'
}

function renderFieldValue(value: unknown): React.ReactNode {
  if (isTriValue(value)) {
    const triRenderers: Record<Tri, () => React.ReactNode> = {
      low: () => <Tag color='red'>Low</Tag>,
      med: () => <Tag color='orange'>Med</Tag>,
      high: () => <Tag color='green'>High</Tag>,
    }
    return triRenderers[value]()
  }

  if (typeof value === 'boolean') {
    return value ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  return '—'
}

interface ComputedSectionViewProps {
  config: ComputedSection
  fieldValues: Record<string, unknown>
  ontologyFields: Record<string, OntologyField>
  onOverrideChange?: (
    overrideValue: boolean | undefined,
    justification: string | undefined
  ) => void
  context?: Record<string, unknown>
}

/**
 * A generic component for rendering computed sections.
 * Displays the input fields, computes the outcome, and allows manual override.
 */
export const ComputedSectionView: FC<ComputedSectionViewProps> = ({
  config,
  fieldValues,
  ontologyFields,
  onOverrideChange,
  context,
}) => {
  const { isStepComplete } = useTicketStepContext()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    computedResult,
    isOverridden,
    overrideValue,
    finalResult,
    overrideJustification,
    displayFieldValues,
  } = useComputedSectionValue(config, fieldValues, context)

  // Auto-clear override when it matches computed result
  useEffect(() => {
    if (
      config.allowOverride &&
      overrideValue !== undefined &&
      computedResult === overrideValue
    ) {
      onOverrideChange?.(undefined, undefined)
    }
  }, [computedResult, overrideValue, config.allowOverride, onOverrideChange])

  const canEdit = !isStepComplete && config.allowOverride

  const handleSave = useCallback(
    (outcome: boolean | undefined, justification: string | undefined) => {
      onOverrideChange?.(outcome, justification)
    },
    [onOverrideChange]
  )

  const title = config.title ?? 'Outcome'

  // Calculate breakdown for display based on function type
  const computationBreakdown = useMemo(() => {
    const functionSpec = config.functionSpec

    if (isWeightedScoringFunction(config.function)) {
      // Weighted boolean scoring breakdown
      if (!functionSpec.threshold) return null

      let totalScore = 0
      const fieldScores: Array<{
        fieldId: string
        name: string
        value: boolean | undefined
        weight: number
        inverted: boolean
        contributes: boolean
        score: number
      }> = []

      // Only process weighted parameters
      const weightedParams = functionSpec.parameters.filter(
        (p) => 'weight' in p
      ) as Array<{
        field: string
        weight: number
        inverted?: boolean
      }>

      for (const paramSpec of weightedParams) {
        const value = fieldValues[paramSpec.field] as boolean | undefined
        const ontologyField = ontologyFields[paramSpec.field]
        const name = ontologyField?.name || paramSpec.field
        const weight = paramSpec.weight
        const inverted = !!paramSpec.inverted
        const shouldScore = inverted ? !value : value
        const contributes = !!shouldScore && value !== undefined
        const score = contributes ? weight : 0

        totalScore += score

        fieldScores.push({
          fieldId: paramSpec.field,
          name,
          value,
          weight,
          inverted,
          contributes,
          score,
        })
      }

      return {
        type: 'weighted' as const,
        fieldScores,
        totalScore,
        threshold: functionSpec.threshold,
        result: totalScore <= functionSpec.threshold,
      }
    }

    if (isTriStateFunction(config.function)) {
      // Tri-state screening breakdown
      const triStateParams = functionSpec.parameters.filter(
        (p) => 'type' in p && p.type === 'tri-state'
      ) as Array<{
        field: string
        type: 'tri-state'
        defaultValue?: string
      }>

      const parameterValues: Array<{
        fieldId: string
        name: string
        value: 'low' | 'med' | 'high'
      }> = []

      for (const paramSpec of triStateParams) {
        const value = (fieldValues[paramSpec.field] ??
          paramSpec.defaultValue ??
          'low') as 'low' | 'med' | 'high'
        const ontologyField = ontologyFields[paramSpec.field]
        const name = ontologyField?.name || paramSpec.field

        parameterValues.push({
          fieldId: paramSpec.field,
          name,
          value,
        })
      }

      return {
        type: 'tristate' as const,
        parameterValues,
        result: computedResult ?? false,
      }
    }

    return null
  }, [config, fieldValues, ontologyFields, computedResult])

  // Get display fields with their labels, tooltips, and parameter info
  // Auto-derive display fields from functionSpec if displayFields not specified or empty
  const fieldsToDisplay =
    config.displayFields.length > 0
      ? config.displayFields
      : config.functionSpec.parameters.map((p) => p.field)

  const displayItems = fieldsToDisplay.map((fieldId) => {
    const ontologyField = ontologyFields[fieldId]
    const label = ontologyField?.name ?? fieldId
    const value = displayFieldValues[fieldId]
    // Get tooltip configuration if field is an input field
    const inputField =
      ontologyField?.category === FieldCategory.INPUT
        ? (ontologyField as InputField)
        : undefined
    const tooltipConfig = inputField?.tooltip

    // Get parameter info from functionSpec
    const paramSpec = config.functionSpec.parameters.find(
      (p) => p.field === fieldId
    )
    let parameterType: 'weighted' | 'tristate' | 'unknown' = 'unknown'
    let weight = 0
    let inverted = false

    if (paramSpec) {
      if ('weight' in paramSpec) {
        parameterType = 'weighted'
        weight = paramSpec.weight
        inverted = !!paramSpec.inverted
      } else if ('type' in paramSpec && paramSpec.type === 'tri-state') {
        parameterType = 'tristate'
      }
    }

    const shouldScore = inverted ? !value : value
    const contributes = !!shouldScore && value !== undefined
    const score = contributes ? weight : 0

    return {
      fieldId,
      label,
      value,
      tooltipConfig,
      parameterType,
      weight,
      inverted,
      contributes,
      score,
    }
  })

  // Render field label with optional tooltip and parameter info
  const renderFieldLabel = (
    label: string,
    tooltipConfig: TooltipConfig | undefined,
    parameterType: 'weighted' | 'tristate' | 'unknown',
    weight?: number
  ) => {
    const labelWithInfo = (
      <span>
        {label}
        {parameterType === 'weighted' && weight !== undefined && (
          <Typography.Text
            type='secondary'
            style={{
              fontSize: '11px',
              marginLeft: '8px',
              fontWeight: 500,
              color: '#8c8c8c',
            }}>
            (Weight: {weight})
          </Typography.Text>
        )}
        {parameterType === 'tristate' && (
          <Typography.Text
            type='secondary'
            style={{
              fontSize: '11px',
              marginLeft: '8px',
              fontWeight: 500,
              color: '#1890ff',
            }}>
            (Tri-state)
          </Typography.Text>
        )}
      </span>
    )

    if (tooltipConfig) {
      return (
        <LabelWithTooltipContainer>
          {labelWithInfo}
          <DynamicFieldTooltip tooltip={tooltipConfig} />
        </LabelWithTooltipContainer>
      )
    }

    return labelWithInfo
  }

  // Render field value with proper color coding based on parameter type
  const renderFieldValueWithScoring = (
    value: unknown,
    parameterType: 'weighted' | 'tristate' | 'unknown',
    inverted: boolean = false
  ) => {
    if (parameterType === 'tristate' && isTriValue(value)) {
      // Tri-state values with appropriate colors
      const triRenderers: Record<Tri, () => React.ReactNode> = {
        low: () => <Tag color='red'>Low</Tag>,
        med: () => <Tag color='orange'>Med</Tag>,
        high: () => <Tag color='green'>High</Tag>,
      }
      return triRenderers[value]()
    }

    if (parameterType === 'weighted' && typeof value === 'boolean') {
      // For normal questions: Yes = bad (red), No = good (green)
      // For inverted questions: Yes = good (green), No = bad (red)
      const isGoodAnswer = inverted ? value : !value
      const color = isGoodAnswer ? 'success' : 'error'
      const text = value ? 'Yes' : 'No'

      return <Tag color={color}>{text}</Tag>
    }

    return renderFieldValue(value)
  }

  // Sub-component for displaying input field descriptions
  interface FieldDescriptionsProps {
    displayItems: Array<{
      fieldId: string
      label: string
      value: unknown
      tooltipConfig: TooltipConfig | undefined
      parameterType: 'weighted' | 'tristate' | 'unknown'
      weight: number
      inverted: boolean
    }>
    renderFieldLabel: (
      label: string,
      tooltipConfig: TooltipConfig | undefined,
      parameterType: 'weighted' | 'tristate' | 'unknown',
      weight?: number
    ) => React.ReactNode
    renderFieldValueWithScoring: (
      value: unknown,
      parameterType: 'weighted' | 'tristate' | 'unknown',
      inverted?: boolean
    ) => React.ReactNode
  }

  const FieldDescriptions: FC<FieldDescriptionsProps> = ({
    displayItems,
    renderFieldLabel,
    renderFieldValueWithScoring,
  }) => {
    if (displayItems.length === 0) return null

    return (
      <Descriptions column={1} bordered>
        {displayItems.map(
          ({
            fieldId,
            label,
            value,
            tooltipConfig,
            parameterType,
            weight,
            inverted,
          }) => (
            <Descriptions.Item
              key={fieldId}
              label={renderFieldLabel(
                label,
                tooltipConfig,
                parameterType,
                weight
              )}>
              {renderFieldValueWithScoring(value, parameterType, inverted)}
            </Descriptions.Item>
          )
        )}
      </Descriptions>
    )
  }

  // Sub-component for the outcome display and override functionality
  interface OutcomeDisplayProps {
    finalResult: boolean | undefined
    canEdit: boolean
    onEditClick: () => void
    isOverridden: boolean
    overrideJustification?: string
  }

  const OutcomeDisplay: FC<OutcomeDisplayProps> = ({
    finalResult,
    canEdit,
    onEditClick,
    isOverridden,
    overrideJustification,
  }) => {
    const result = finalResult ?? false

    return (
      <>
        <Tooltip title={canEdit ? 'Click to override outcome' : undefined}>
          <OutcomeSplash
            $pass={result}
            onClick={() => canEdit && onEditClick()}
            style={{ cursor: canEdit ? 'pointer' : 'default' }}>
            {result ? (
              <CheckCircleOutlined style={{ fontSize: 56, color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ fontSize: 56, color: '#ff4d4f' }} />
            )}
            <OutcomeLabel>{result ? 'Pass' : 'Fail'}</OutcomeLabel>
          </OutcomeSplash>
        </Tooltip>
        {isOverridden && overrideJustification && (
          <EditRow>
            <Alert
              type='warning'
              showIcon
              message={`Overridden: ${overrideJustification}`}
            />
          </EditRow>
        )}
      </>
    )
  }

  // Sub-component for computation breakdown display
  interface ComputationBreakdownProps {
    breakdown:
      | {
          type: 'weighted'
          fieldScores: Array<{
            fieldId: string
            name: string
            value: boolean | undefined
            score: number
          }>
          totalScore: number
          threshold: number
          result: boolean
        }
      | {
          type: 'tristate'
          parameterValues: Array<{
            fieldId: string
            name: string
            value: 'low' | 'med' | 'high'
          }>
          result: boolean
        }
      | null
  }

  const ComputationBreakdown: FC<ComputationBreakdownProps> = ({
    breakdown,
  }) => {
    if (!breakdown) return null

    return (
      <Space direction='vertical' align='center' style={{ marginTop: '16px' }}>
        {breakdown.type === 'weighted' && (
          <Space>
            <Typography.Text strong>Total Score: </Typography.Text>
            <Tag
              color={
                breakdown.totalScore > breakdown.threshold ? 'red' : 'green'
              }
              style={{ fontSize: '14px', padding: '4px 8px' }}>
              {breakdown.totalScore}
            </Tag>
            <Typography.Text strong>Threshold: </Typography.Text>
            <Tag color='blue' style={{ fontSize: '14px', padding: '4px 8px' }}>
              ≤ {breakdown.threshold}
            </Tag>
          </Space>
        )}
        {breakdown.type === 'tristate' && (
          <Space direction='vertical' align='center'>
            <Typography.Text strong style={{ color: '#1890ff' }}>
              Screening Decision Matrix
            </Typography.Text>
            <Typography.Text
              type='secondary'
              style={{ fontSize: '12px', textAlign: 'center' }}>
              Based on tri-state parameter combination
            </Typography.Text>
          </Space>
        )}
      </Space>
    )
  }

  return (
    <StyledCard title={title}>
      <TwoColumn>
        <LeftColumn>
          <FieldDescriptions
            displayItems={displayItems}
            renderFieldLabel={renderFieldLabel}
            renderFieldValueWithScoring={renderFieldValueWithScoring}
          />
        </LeftColumn>
        <RightColumn>
          <OutcomeDisplay
            finalResult={finalResult}
            canEdit={canEdit ?? false}
            onEditClick={() => setIsModalOpen(true)}
            isOverridden={isOverridden}
            overrideJustification={overrideJustification}
          />
          <ComputationBreakdown breakdown={computationBreakdown} />
        </RightColumn>
      </TwoColumn>

      {config.allowOverride && (
        <ComputedOutcomeModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          computedOutcome={computedResult ?? false}
          currentOverride={overrideValue}
          currentJustification={overrideJustification}
          onSave={handleSave}
        />
      )}
    </StyledCard>
  )
}

export default React.memo(ComputedSectionView)
